import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { SectionKicker, SectionHeading, FadeIn } from "@/components/shared/SectionBits";

export default function PostListPage({ type, title, kicker, description }) {
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [params, setParams] = useSearchParams();
    const q = params.get("q") || "";
    const category = params.get("category") || "";
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState(q);

    useEffect(() => {
        setLoading(true);
        api.get("/posts", { params: { type, q: q || undefined, category: category || undefined, limit: 100 } })
            .then((r) => setPosts(r.data))
            .finally(() => setLoading(false));
    }, [type, q, category]);

    useEffect(() => {
        api.get("/posts/categories", { params: { type } }).then((r) => setCategories(r.data));
    }, [type]);

    const submitSearch = (e) => {
        e.preventDefault();
        const next = new URLSearchParams(params);
        if (search) next.set("q", search); else next.delete("q");
        setParams(next, { replace: true });
    };

    const setCategory = (c) => {
        const next = new URLSearchParams(params);
        if (c) next.set("category", c); else next.delete("category");
        setParams(next, { replace: true });
    };

    const linkBase = type === "news" ? "/news" : "/blog";

    return (
        <div data-testid={`${type}-list-page`}>
            <section className="dr-container pt-32 pb-14">
                <SectionKicker>{kicker}</SectionKicker>
                <motion.h1
                    initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
                    className="font-display font-bold text-dr-navy text-5xl md:text-6xl lg:text-7xl leading-[1] max-w-4xl"
                >
                    {title}
                </motion.h1>
                <p className="text-dr-dark/75 text-lg md:text-xl mt-6 max-w-2xl">{description}</p>

                <div className="mt-12 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
                    <form onSubmit={submitSearch} className="relative w-full md:w-96">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-dr-dark/50" />
                        <input
                            data-testid={`${type}-search-input`}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={`Search ${type === "news" ? "news" : "insights"}...`}
                            className="w-full pl-11 pr-4 py-3.5 rounded-full border border-dr-silver bg-white text-sm focus:border-dr-blue"
                        />
                    </form>

                    <div className="flex flex-wrap gap-2">
                        <button
                            data-testid={`${type}-category-all`}
                            onClick={() => setCategory("")}
                            className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                                !category ? "bg-dr-navy text-white border-dr-navy" : "bg-white text-dr-navy border-dr-silver hover:border-dr-navy"
                            }`}
                        >
                            All
                        </button>
                        {categories.map((c) => (
                            <button
                                key={c}
                                data-testid={`${type}-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => setCategory(c)}
                                className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                                    category === c ? "bg-dr-navy text-white border-dr-navy" : "bg-white text-dr-navy border-dr-silver hover:border-dr-navy"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="dr-container pb-28">
                {loading ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="h-96 rounded-2xl bg-dr-grey animate-pulse" />
                        ))}
                    </div>
                ) : posts.length === 0 ? (
                    <p className="py-20 text-center text-dr-dark/60">No articles match your filters.</p>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {posts.map((p, i) => (
                            <FadeIn key={p.slug} delay={i * 0.05}>
                                <Link
                                    to={`${linkBase}/${p.slug}`}
                                    data-testid={`${type}-post-${p.slug}`}
                                    className="group block h-full bg-white border border-dr-silver rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(8,41,74,0.10)] transition-all duration-500"
                                >
                                    <div className="aspect-[16/10] overflow-hidden bg-dr-grey">
                                        {p.cover_image && (
                                            <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        )}
                                    </div>
                                    <div className="p-7">
                                        <div className="flex items-center gap-3">
                                            <p className="text-[11px] tracking-[0.24em] uppercase text-dr-blue font-medium">{p.category}</p>
                                            <span className="text-dr-silver">·</span>
                                            <p className="text-[11px] text-dr-dark/60 uppercase tracking-widest">{p.read_minutes} min</p>
                                        </div>
                                        <h3 className="font-display text-xl font-semibold text-dr-navy mt-3 leading-snug">{p.title}</h3>
                                        <p className="text-sm text-dr-dark/70 mt-3 leading-relaxed line-clamp-3">{p.excerpt}</p>
                                        <div className="mt-6 flex items-center justify-between">
                                            <p className="text-xs text-dr-dark/60">By {p.author}</p>
                                            <ArrowUpRight className="w-4 h-4 text-dr-navy/60 group-hover:text-dr-navy group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                                        </div>
                                    </div>
                                </Link>
                            </FadeIn>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
